import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Download, Home, Wallet, Calendar, TrendingUp, ShieldCheck, CreditCard } from "lucide-react";
import { API } from "@/lib/api";
import HardshipPanel from "@/components/HardshipPanel";
import LegacyPlanner from "@/components/LegacyPlanner";
import QuoteLocksPanel from "@/components/QuoteLocksPanel";
import SeasonalPaymentPanel from "@/components/SeasonalPaymentPanel";
import BuildPhasePanel from "@/components/BuildPhasePanel";
import { GroupSharePanel, ReferralPanel } from "@/components/GroupAndReferral";
import { WishlistPanel, AccountPanel } from "@/components/AccountAndWishlist";
import MilestoneTimeline from "@/components/MilestoneTimeline";

const money = (v, cur = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0);

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const [property, setProperty] = useState(null);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading || !user || !user.email) return;
    (async () => {
      try {
        const [p, pay, s, sch] = await Promise.all([
          api.get("/log/property"),
          api.get("/log/payments"),
          api.get("/log/summary"),
          api.get("/log/schedule?count=6"),
        ]);
        setProperty(p.data);
        setPayments(pay.data.payments || []);
        setSummary(s.data);
        setSchedule(sch.data.schedule || []);
      } finally { setBusy(false); }
    })();
  }, [loading, user]);

  const downloadStatement = async () => {
    try {
      const token = localStorage.getItem("mv_token");
      const res = await fetch(`${API}/log/statement.pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MAVULA-Statement-${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Could not download statement — please try again in a moment.");
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  const cur = summary?.currency || "GBP";
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16" data-testid="dashboard-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="mv-eyebrow"><span style={{color:"var(--mv-gold)"}}>Personal Log</span></div>
          <h1 className="font-display text-3xl md:text-4xl mv-green-text mt-2">
            Welcome, {user?.name || "Owner"}
          </h1>
          <p className="text-[14.5px] mt-1" style={{color:"var(--mv-muted)"}}>Your private, encrypted property record — always current.</p>
        </div>
        <button className="btn-ghost" data-testid="dashboard-download-btn" onClick={downloadStatement}><Download size={16}/> Download Statement</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <Link to="/pay?type=deposit" className="btn-gold" data-testid="dashboard-pay-deposit-btn">
          <CreditCard size={16}/> Pay Deposit
        </Link>
        <Link to="/pay?type=instalment" className="btn-green" data-testid="dashboard-pay-instalment-btn">
          <CreditCard size={16}/> Pay Monthly Instalment
        </Link>
      </div>

      {/* Milestone timeline — visible proof money is released against progress */}
      <div className="mt-8">
        <MilestoneTimeline progressPct={summary?.progress_percent || 0} />
      </div>

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { i: <Home size={18}/>, k: "Total Price", v: money(summary?.total_price, cur) },
          { i: <Wallet size={18}/>, k: "Paid So Far", v: money(summary?.total_paid, cur) },
          { i: <TrendingUp size={18}/>, k: "Remaining", v: money(summary?.remaining, cur) },
          { i: <Calendar size={18}/>, k: "Monthly", v: `${money(summary?.monthly_payment, cur)}` },
        ].map((c) => (
          <div key={c.k} className="mv-card p-5">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center mv-green-text mb-3" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
            <div className="text-[11px] uppercase tracking-[0.2em]" style={{color:"var(--mv-muted)"}}>{c.k}</div>
            <div className="font-display text-3xl mv-green-text mt-1">{busy ? "…" : c.v}</div>
          </div>
        ))}
      </div>

      {/* Property + Progress */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 mv-card p-6">
          <div className="mv-eyebrow">Your Property</div>
          <h2 className="font-display text-2xl mv-green-text mt-2">{property?.title || "—"}</h2>
          <p className="text-[14px] mt-1" style={{color:"var(--mv-muted)"}}>{property?.location}</p>
          <div className="mt-6">
            <div className="flex justify-between text-[13px] mb-2">
              <span style={{color:"var(--mv-muted)"}}>Build Progress</span>
              <span className="mv-green-text font-semibold">{property?.progress_percent || 0}%</span>
            </div>
            <div className="h-3 rounded-full bg-black/5 overflow-hidden">
              <div className="h-full" style={{ width: `${property?.progress_percent || 0}%`, background: "var(--mv-gold)" }} />
            </div>
            <div className="mt-3 text-[13.5px]" style={{color:"var(--mv-muted)"}}>
              Status: <span className="mv-green-text font-semibold">{property?.status || "—"}</span> · Next: {property?.next_milestone || "—"}
            </div>
          </div>
        </div>

        <div className="mv-card p-6">
          <div className="mv-eyebrow">Terms</div>
          <ul className="mt-3 space-y-3 text-[14px]">
            <li className="flex justify-between"><span style={{color:"var(--mv-muted)"}}>Deposit</span><span className="mv-green-text font-semibold">15%</span></li>
            <li className="flex justify-between"><span style={{color:"var(--mv-muted)"}}>Interest (per contract)</span><span className="mv-green-text font-semibold">As signed</span></li>
            <li className="flex justify-between"><span style={{color:"var(--mv-muted)"}}>Term</span><span className="mv-green-text font-semibold">{property?.term_years || 10} years</span></li>
            <li className="flex justify-between"><span style={{color:"var(--mv-muted)"}}>Rate lock</span><span className="mv-green-text font-semibold">First 5 years</span></li>
            <li className="flex justify-between"><span style={{color:"var(--mv-muted)"}}>Payments made</span><span className="mv-green-text font-semibold">{summary?.payments_count ?? 0}</span></li>
          </ul>
          <div className="mt-6 text-[13px] flex items-start gap-2" style={{color:"var(--mv-muted)"}}>
            <ShieldCheck size={16} style={{color:"var(--mv-gold)"}} className="mt-0.5"/> Title deed will be registered in your name once fully paid.
          </div>
        </div>
      </div>

      {/* Upcoming Payments — schedule + reminder note */}
      <div className="mt-10 mv-card overflow-hidden" data-testid="dashboard-schedule">
        <div className="p-5 md:p-6 border-b mv-hairline bg-[var(--mv-bg)] flex flex-wrap justify-between items-start gap-3">
          <div>
            <div className="mv-eyebrow"><span style={{color:"var(--mv-gold)"}}>Payment Schedule</span></div>
            <h3 className="font-display text-xl mv-green-text mt-1">Your next 6 instalments — automatic reminders sent 3 days before each.</h3>
          </div>
          <Link to="/pay?type=instalment" className="btn-gold !py-2" data-testid="schedule-pay-now"><CreditCard size={14}/> Pay Next Now</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-y md:divide-y-0 md:divide-x mv-hairline">
          {schedule.length === 0 && !busy && (
            <div className="col-span-full p-8 text-center text-neutral-500 text-[14px]">No upcoming instalments scheduled.</div>
          )}
          {schedule.map((s, i) => (
            <div key={i} className={`p-4 md:p-5 ${i === 0 ? "bg-[rgba(212,175,55,0.10)]" : ""}`}>
              <div className="text-[10.5px] uppercase tracking-[0.2em]" style={{color:"var(--mv-muted)"}}>
                {i === 0 ? "Next Due" : `Due ${i === 1 ? "after" : "in " + i + " months"}`}
              </div>
              <div className="font-display text-[15px] mv-green-text mt-1">{s.date}</div>
              <div className="mt-1 font-semibold">{money(s.amount, s.currency)}</div>
              <div className="mt-2 text-[11px]" style={{color:"var(--mv-muted)"}}>{s.type}</div>
            </div>
          ))}
        </div>
        <div className="p-4 md:p-5 border-t mv-hairline text-[12.5px] flex flex-wrap items-center gap-2" style={{color:"var(--mv-muted)"}}>
          <ShieldCheck size={14} style={{color:"var(--mv-gold)"}}/> Reminder emails are sent automatically to your registered email 3 days before each due date. Every payment made triggers an instant receipt email.
        </div>
      </div>

      {/* Payments */}
      <div className="mt-10 mv-card overflow-hidden">
        <div className="p-5 md:p-6 border-b mv-hairline bg-[var(--mv-bg)] flex justify-between items-center">
          <div>
            <div className="mv-eyebrow">Payment History</div>
            <h3 className="font-display text-xl mv-green-text mt-1">Every receipt, every reference — permanent record.</h3>
          </div>
          <div className="text-[13px]" style={{color:"var(--mv-muted)"}}>
            {payments.length} payment{payments.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]" data-testid="payments-table">
            <thead>
              <tr className="text-left border-b mv-hairline" style={{color:"var(--mv-muted)"}}>
                <th className="py-3 px-5 font-medium">Date</th>
                <th className="py-3 px-5 font-medium">Type</th>
                <th className="py-3 px-5 font-medium">Reference</th>
                <th className="py-3 px-5 font-medium">Status</th>
                <th className="py-3 px-5 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b mv-hairline last:border-0">
                  <td className="py-3 px-5">{p.date}</td>
                  <td className="py-3 px-5 font-semibold mv-green-text">{p.type}</td>
                  <td className="py-3 px-5" style={{color:"var(--mv-muted)"}}>{p.reference}</td>
                  <td className="py-3 px-5">
                    <span className="text-[12px] px-2 py-0.5 rounded-full" style={{background:"rgba(37,211,102,0.14)", color:"#0A7A2C"}}>{p.status}</span>
                  </td>
                  <td className="py-3 px-5 text-right font-semibold">{money(p.amount, cur)}</td>
                </tr>
              ))}
              {!payments.length && !busy && (
                <tr><td colSpan={5} className="py-10 text-center text-neutral-500">No payments recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quote locks (if any) */}
      <div className="mt-10">
        <QuoteLocksPanel />
      </div>

      {/* Hardship + Legacy Planner */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HardshipPanel />
        <LegacyPlanner />
      </div>

      {/* Seasonal + Build Phase */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SeasonalPaymentPanel />
        <BuildPhasePanel />
      </div>

      {/* Group + Referral */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GroupSharePanel />
        <ReferralPanel />
      </div>

      {/* Wishlist + Account controls */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WishlistPanel />
        <AccountPanel />
      </div>
    </div>
  );
}
